"use client";
import useTranslate from "@app/_hooks/useTranslate";

import { Input, Select } from "@components/inputs";
import { Button } from "@components/ngg";
import { useForm, ValidationError } from "@formspree/react";
import type { Maybe } from "@server/gql/graphql";
import { useCallback, useMemo, useState } from "react";
import xss from "xss";

import { cn } from "@/lib/utils";

const FORMSPREE_BOOK_MEETING_FORM_ID = "xnqkover";

export default function BookMeeting({
  className,
  reference,
}: {
  className?: string;
  reference?: Maybe<string>;
}) {
  const t = useTranslate();
  const [state, handleSubmit] = useForm(
    reference || FORMSPREE_BOOK_MEETING_FORM_ID,
  );
  const [email, setEmail] = useState<string | undefined>(undefined);

  function handleChange(event: React.ChangeEvent<HTMLInputElement>) {
    setEmail(event.target.value);
  }

  const isInputError = useCallback(
    (inputName: string) => {
      if (!state.errors) return false;
      if (Object.values(state.errors).length === 0) return false;
      return state.errors.getFieldErrors(inputName).length > 0;
    },
    [state.errors],
  );

  const consultations = useMemo(
    () => [
      {
        id: 1,
        value: t("stockholm"),
        label: t("stockholm"),
      },
      {
        id: 2,
        value: t("gothenburg"),
        label: t("gothenburg"),
      },
      {
        id: 3,
        value: t("malmo"),
        label: t("malmo"),
      },
      {
        id: 4,
        value: t("digitalMeeting"),
        label: t("digitalMeeting"),
      },
    ],
    [t],
  );

  const bookMeetingFormSuccessText = useMemo(
    () =>
      t("bookMeetingFormSuccessText", {
        EMAIL: `<strong>${email}</strong>`,
      }) ?? "",
    [email, t],
  );

  if (state.succeeded) {
    return (
      <>
        <h2 className="text-lg">{t("bookMeetingFormSuccessHeading")}</h2>
        <p
          className="mt-4 text-base"
          dangerouslySetInnerHTML={{
            __html: xss(bookMeetingFormSuccessText),
          }}
        />
      </>
    );
  }

  return (
    <form onSubmit={handleSubmit} className={cn("max-w-4xl", className)}>
      {t("bookMeetingFormHeading", { strict: true }) && (
        <h3 className="w-full text-lg font-normal">
          {t("bookMeetingFormHeading")}
        </h3>
      )}
      <div className="mt-6 space-y-3">
        <Input
          id="name"
          name="name"
          type="text"
          required
          fullWidth
          label={t("bookMeetingFormNameLabel") || t("name")}
          classNames={{
            input: isInputError("name") ? "border border-error" : "",
          }}
        />
        <ValidationError
          className="mt-2 text-base"
          field="name"
          errors={state.errors}
        />
        <Input
          id="email"
          name="email"
          type="email"
          required
          fullWidth
          label={t("bookMeetingFormEmailLabel") || t("email")}
          classNames={{
            input: isInputError("email") ? "border border-error" : "",
          }}
          onChange={(event) => handleChange(event)}
        />
        <ValidationError
          className="mt-2 text-base"
          field="email"
          errors={state.errors}
        />
        <Input
          id="phoneNumber"
          name="phoneNumber"
          type="tel"
          fullWidth
          label={t("bookMeetingFormPhoneNumberLabel") || t("phoneNumber")}
          classNames={{
            input: isInputError("phoneNumber") ? "border border-error" : "",
          }}
        />
        <ValidationError
          className="mt-2 text-base"
          field="phoneNumber"
          errors={state.errors}
        />
        <Select
          name="consultation"
          placeholder={
            t("bookMeetingFormConsultationPlaceholder") || t("consultation")
          }
          options={consultations}
        >
          <ValidationError
            className="mt-2 text-base"
            field="room"
            errors={state.errors}
          />
        </Select>
      </div>
      <Button
        type="submit"
        isLoading={state.submitting}
        fullWidth
        className="btn-primary mt-6"
      >
        {t("bookMeetingFormSubmitButton") || t("send")}
      </Button>
      {/* Honeypot */}
      <input type="text" name="_gotcha" className="hidden" />
    </form>
  );
}

export function BookMeetingPopup() {
  return (
    <div className="mx-auto max-w-[420px] bg-white px-4 py-8 md:px-6">
      <BookMeeting className="mt-4" />
    </div>
  );
}
